import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ChecklistStatusDropdownOptions,
  ChecklistStatusDropdownConfig,
  checklistLastClickedKey,
  getDrilldownType,
  NumberToStatus,
  pathDelmiter,
  Status,
  tasksddLastClickedKey,
  tasksddScrollKey,
  URLs,
} from "../../../Constants/Consts";
import Select, { components } from "react-select";
import useCheckList from "../../../Helpers/CustomHooks/useStoredCheckListObject";
import { fetchApi } from "../../../Helpers/Functions";
import List from "../../../components/CoreComponents/List";
import { updateCheckListItem } from "../../../store/SequenceObject";
import { setModal } from "../../../store/Utilities";
import { RootState } from "../../../store/store";
import { DDCheckList } from "../../../types/DataDD/DataDD";
import { ChecklistLastClicked } from "../../../types/types";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "../../../Helpers/Functions";
import { AddChecklistTaskForm } from "../../../components/AddChecklistTaskForm/AddChecklistTaskForm";

// UI Elements
import "./CheckListStyles.css";
import { twMerge } from "tailwind-merge";
import Breadcrumb from "../../../components/CoreComponents/Breadcrumb";
import SearchComponent from "../../../components/CoreComponents/Search";
import { toast } from "react-toastify";
import { BiReset } from "react-icons/bi";
import { FaRegCheckSquare, FaRegEdit } from "react-icons/fa";
import { FaRegRectangleXmark } from "react-icons/fa6";
import { GrStatusGood } from "react-icons/gr";
import { MdOutlineClear } from "react-icons/md";
import { RiArrowRightSLine } from "react-icons/ri";
import useDebouncedEffect from "../../../hooks/useDebouncedEffect";

export const searchTermLocalStorageKey = "checklist_search_term";
export const filterLocalStorageKey = "checklist_filter";
export const statusFilterLocalStorageKey = "checklist_status_filter";
export const incompletePLFilterLocalStorageKey = "checklist_incomplete_pl_filter";

const validStatusIds = ChecklistStatusDropdownOptions.map((o) => o.value);

const CheckboxOption = (props: any) => (
  <components.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      onChange={() => null}
      style={{ marginRight: 8 }}
    />
    {props.label}
  </components.Option>
);

export const IconOrLoader = ({ Icon, Loader, showLoader }) => {
  return <>{showLoader ? Loader : Icon}</>;
};

const CheckListDD = () => {
  const { path } = useParams();
  const navigate = useNavigate();
  const { SelectedUser } = useSelector(
    (state: RootState) => state.SelectedUser,
  );
  const {
    checkListObject,
    markPassed,
    navigateToPunchList,
    markNotApplicable,
    reset,
  } = useCheckList({ path });
  const drilldownType = getDrilldownType(SelectedUser.disciplineId);
  const { DrilldownMappingsAPIResponse } = useSelector(
    (state: RootState) => state.SequenceObject,
  );

  const BulkActions = {
    MARK_PASS_ALL: { id: "1", name: "Passed" },
    RESET_ALL: { id: "3", name: "Reset Status" },
    MARK_ALL_NOT_APPLICABLE: { id: "4", name: "Not Applicable" },
  };
  const { ProjectPunchLists } = useSelector(
    (state: RootState) => state.PunchList,
  );
  const [currentBulkAction, setCurrentBulkAction] = useState(
    BulkActions.MARK_PASS_ALL.id,
  );
  const [selectAllEnabled, setSelectAllEnabled] = useState(true);
  const [selectedCheckLists, setSelectedCheckLists] = useState<DDCheckList[]>(
    [],
  );
  const [bulkEditEnabled, setBulkEditEnabled] = useState(false);
  const [searchInspectionTask, setSearchInspectionTask] = useState(() => {
    const cached = getLocalStorageItem<string>(searchTermLocalStorageKey);
    return cached ?? "";
  });
  const [selectedFilter, setSelectedFilter] = useState(() => {
    const cached = getLocalStorageItem<string>(filterLocalStorageKey);
    return cached ?? "";
  });
  const [statusFilter, setStatusFilter] = useState<number[]>(() => {
    const cached = getLocalStorageItem<number | number[]>(
      statusFilterLocalStorageKey,
    );
    // Backward compat: old format was a single number
    if (typeof cached === "number") {
      return validStatusIds.includes(cached) ? [cached] : [];
    }
    if (Array.isArray(cached)) {
      return cached.filter((v) => validStatusIds.includes(v));
    }
    return [];
  });
  const [showOnlyIncompletePL, setShowOnlyIncompletePL] = useState<boolean>(
    () => {
      const cached = getLocalStorageItem<boolean>(
        incompletePLFilterLocalStorageKey,
      );
      return cached === true;
    },
  );
  const [filteredCheckList, setFilteredCheckList] = useState(
    Object.keys(checkListObject ?? {}).map((k) => checkListObject[k]),
  );

  const { MPCStatus } = useSelector((state: RootState) => state.SequenceObject);

  // Clean up localStorage when navigating away from CheckList pages entirely
  useEffect(() => {
    return () => {
      // Cleanup when leaving CheckList-related pages
      const currentPath = window.location.pathname;
      const isNavigatingToDetails = currentPath.includes(
        "/CheckListDetailsDD/",
      );
      const isStayingInCheckList = currentPath.includes("/CheckListDD/");

      // Only clear localStorage if leaving the checklist sections entirely
      if (!isNavigatingToDetails && !isStayingInCheckList) {
        removeLocalStorageItem(searchTermLocalStorageKey);
        removeLocalStorageItem(filterLocalStorageKey);
        removeLocalStorageItem(statusFilterLocalStorageKey);
        removeLocalStorageItem(incompletePLFilterLocalStorageKey);
        removeLocalStorageItem(checklistLastClickedKey);
        // Also reset the drill-down highlight/scroll, unless we're heading back
        // to the drill-down page itself (the drill-in -> Back round-trip must
        // keep it). This covers the case where the user exits the checklist
        // directly (e.g. via top nav) without going back through TasksDD.
        if (!currentPath.includes("/TasksDD")) {
          removeLocalStorageItem(tasksddLastClickedKey);
          removeLocalStorageItem(tasksddScrollKey);
        }
      }
    };
  }, []);

  // Clean up localStorage when search/filter are cleared
  useEffect(() => {
    const shouldClearSearch = !searchInspectionTask?.length;
    const shouldClearFilter = !selectedFilter?.length;
    const shouldClearStatus = statusFilter.length === 0;
    const shouldClearIncompletePL = !showOnlyIncompletePL;
    if (shouldClearSearch) removeLocalStorageItem(searchTermLocalStorageKey);
    if (shouldClearFilter) removeLocalStorageItem(filterLocalStorageKey);
    if (shouldClearStatus) removeLocalStorageItem(statusFilterLocalStorageKey);
    if (shouldClearIncompletePL)
      removeLocalStorageItem(incompletePLFilterLocalStorageKey);
  }, [searchInspectionTask, selectedFilter, statusFilter, showOnlyIncompletePL]);

  const incompletePLCounts = useMemo(() => {
    const incompletePLStatuses = [Status.ReadyForReInspection, Status.Pending, Status.OnHold, Status.MaterialsReceived, Status.CompletedWithRootCauseConflict];
    const counts: Record<string, number> = {};
    const seen = new Set<string>();
    for (const pl of ProjectPunchLists) {
      if (pl.ctUuid && incompletePLStatuses.includes(pl.statusId) && !pl.isArchived) {
        const plId = pl.punchListUuid?.toUpperCase();
        if (plId && seen.has(plId)) continue;
        if (plId) seen.add(plId);
        const key = pl.ctUuid.toUpperCase();
        counts[key] = (counts[key] ?? 0) + 1;
      }
    }
    return counts;
  }, [ProjectPunchLists]);

  const getFilteredCheckList = useCallback(
    function () {
      const trimmedSearch = searchInspectionTask?.trim().toLowerCase() ?? "";
      return (
        Object.keys(checkListObject ?? {})
          .map((k) => checkListObject[k])
          .filter((checkList) => {
            const radLabel =
              checkList.drilldownTypeId === 21
                ? [
                    checkList.dd02,
                    checkList.dd03,
                    checkList.ap01,
                    checkList.ap02,
                    checkList.dd04,
                  ]
                    .filter((v) => v != null && v !== "")
                    .join(" - ")
                    .toLowerCase()
                : null;
            const matchesSearch =
              checkList.taskDescription
                ?.toLowerCase()
                .includes(trimmedSearch) ||
              checkList.taskHeader?.toLowerCase().includes(trimmedSearch) ||
              radLabel?.includes(trimmedSearch);

            const matchesHeaderFilter = selectedFilter
              ? (checkList.taskHeader?.trim() || "") === selectedFilter
              : true;

            const matchesStatusFilter =
              statusFilter.length === 0 ||
              statusFilter.includes(checkList.statusId);

            const matchesIncompletePL =
              !showOnlyIncompletePL ||
              (incompletePLCounts[checkList.ctUuid?.toUpperCase()] ?? 0) > 0;

            return (
              matchesSearch &&
              matchesHeaderFilter &&
              matchesStatusFilter &&
              matchesIncompletePL
            );
          })
          // columnOrder is listOrder based on mapping in DB
          .sort((a, b) => a.listOrder - b.listOrder)
      );
    },
    [
      checkListObject,
      searchInspectionTask,
      selectedFilter,
      statusFilter,
      showOnlyIncompletePL,
      incompletePLCounts,
    ],
  );

  // Keep a ref so debounced/immediate effects always call the latest version
  // without stale closures and without adding it to both dep arrays.
  const getFilteredCheckListRef = useRef(getFilteredCheckList);
  getFilteredCheckListRef.current = getFilteredCheckList;

  // Debounced — only for text search (typing)
  useDebouncedEffect(
    () => {
      setSelectedCheckLists([]);
      setFilteredCheckList(getFilteredCheckListRef.current());
    },
    [searchInspectionTask],
    300,
  );

  // Immediate — for button clicks (status/header filter) and data changes
  useEffect(() => {
    setSelectedCheckLists([]);
    setFilteredCheckList(getFilteredCheckListRef.current());
  }, [
    statusFilter,
    selectedFilter,
    checkListObject,
    showOnlyIncompletePL,
    incompletePLCounts,
  ]);

  const filterOptions = useMemo(() => {
    const headersSet = new Set<string>();
    Object.keys(checkListObject ?? {}).forEach((k) => {
      const item = checkListObject[k];
      const taskHeader = item.taskHeader?.trim();
      if (taskHeader) {
        headersSet.add(taskHeader);
      }
    });
    return Array.from(headersSet).sort();
  }, [checkListObject]);

  useEffect(() => {
    if (
      filteredCheckList.every((filteredItem) =>
        selectedCheckLists.some(
          (selectedItem) => selectedItem.ctUuid === filteredItem.ctUuid,
        ),
      )
    ) {
      setSelectAllEnabled(true);
    } else {
      setSelectAllEnabled(false);
    }
  }, [selectedCheckLists, filteredCheckList]);

  function addOrRemoveSelectedItem(item: DDCheckList) {
    if (isTaskDisabled(item.ctUuid)) return;

    const existingItem = selectedCheckLists.find(
      (selectedItem) =>
        selectedItem.ctUuid.toLocaleLowerCase() ===
        item.ctUuid.toLocaleLowerCase(),
    );
    if (existingItem) {
      setSelectedCheckLists(
        selectedCheckLists.filter(
          (selectedItem) => selectedItem !== existingItem,
        ),
      );
    } else {
      setSelectedCheckLists([...selectedCheckLists, item]);
    }
  }

  function addOrRemoveAllSelectedItems() {
    if (selectAllEnabled) {
      setSelectedCheckLists([]);
      return;
    }
    setSelectedCheckLists(
      filteredCheckList.filter((item) => !isTaskDisabled(item.ctUuid)),
    );
  }

  function performBulkOperation() {
    for (const selectedItem of selectedCheckLists) {
      switch (currentBulkAction) {
        case BulkActions.MARK_PASS_ALL.id:
          markPassed(selectedItem);
          break;
        case BulkActions.MARK_ALL_NOT_APPLICABLE.id:
          markNotApplicable(selectedItem);
          break;
        case BulkActions.RESET_ALL.id:
          reset(selectedItem);
          break;
        default:
          console.warn(`Unknown bulk action: ${currentBulkAction}`);
          break;
      }
    }
  }

  const dispatch = useDispatch();
  // Use the actual drilldownTypeId from checklist items (consistent with details page)
  // rather than getDrilldownType(disciplineId) which may return a different mapping
  const firstChecklistItem =
    checkListObject && checkListObject[Object.keys(checkListObject ?? {})[0]];
  const itemDrilldownType =
    firstChecklistItem?.drilldownTypeId ?? drilldownType;
  const DrilldownMappingObject = DrilldownMappingsAPIResponse.find(
    (d) => d.drilldownTypeId === itemDrilldownType,
  );

  const location = useLocation();
  const handleGetCurrentDDLevels = () => {
    const validDisciplines = [2, 3, 5, 10];
    // Split path by delimiter
    const pathArray = decodeURIComponent(location?.pathname)?.split(
      pathDelmiter,
    );
    // Extract just the first segment after "/CheckListDD/"
    pathArray.splice(0, 1, pathArray[0].split("/")[2]);
    // Filter out navigation markers ("childs", "taskChilds") and empty segments;
    // replace "checkItems" with a user-friendly label for RAD type 21 flattened items.
    const cleanedPathArray = pathArray
      .filter((p) => p && p.length > 0 && p !== "childs" && p !== "taskChilds")
      .map((p) => (p === "checkItems" ? "I/O Checks" : p));

    const currentDDSLevels = [];
    for (let i = 0; i < cleanedPathArray.length; i++) {
      const ddLabel = DrilldownMappingObject?.[`dd0${i + 1}`] ?? "";
      const ddValue = cleanedPathArray[i] ?? "";
      if (ddValue) {
        if (currentDDSLevels.length > 0) {
          currentDDSLevels.push({ label: <RiArrowRightSLine />, icon: true });
        }
        currentDDSLevels.push({
          label: ddLabel ? (
            <>
              <strong>{ddLabel}</strong>
              {` : ${ddValue}`}
            </>
          ) : (
            ddValue
          ),
        });
      }
    }
    if (
      validDisciplines.includes(SelectedUser.disciplineId) &&
      filteredCheckList[0]
    ) {
      if (currentDDSLevels.length > 0) {
        currentDDSLevels.push({ label: <RiArrowRightSLine />, icon: true });
      }
      currentDDSLevels.push({
        label: (
          <>
            <strong>Model Number</strong>
            {` : ${filteredCheckList[0]?.["modelNumber"]} - `}
            <strong>Model Description</strong>
            {` : ${filteredCheckList[0]?.["modelDescription"]}`}
          </>
        ),
      });
    }
    return currentDDSLevels;
  };
  const ddLevels = handleGetCurrentDDLevels();
  const breadcrumbTrail = [
    { label: SelectedUser.projectNumber },
    { label: " - " },
    { label: SelectedUser.project },
    { label: <RiArrowRightSLine />, icon: true },
    { label: SelectedUser.discipline },
  ];
  const secondaryTrail = ddLevels;

  // Read the "last clicked" marker once on mount (it only changes when the user
  // clicks a row, which navigates away and unmounts this page). Deriving a pure
  // predicate from it avoids hitting localStorage for every row on every render
  // and for every element scanned during scroll-restore.
  const [lastClicked] = useState(() =>
    getLocalStorageItem<ChecklistLastClicked>(checklistLastClickedKey),
  );
  const selectedCtUuid =
    lastClicked?.path === path ? lastClicked?.ctUuid : undefined;
  const isChecklistItemSelected = useCallback(
    (item: DDCheckList) => item.ctUuid === selectedCtUuid,
    [selectedCtUuid],
  );

  const handleCheckListClick = (item: DDCheckList) => {
    // Remember which task was opened, scoped to this checklist path, so returning
    // highlights the row and scrolls it back into view.
    setLocalStorageItem(checklistLastClickedKey, { path, ctUuid: item.ctUuid });
    navigate(
      `/CheckListDetailsDD/${encodeURIComponent(path)}${pathDelmiter}${item.ctUuid}`,
    );
  };

  const isTaskDisabled = (taskId) => {
    const statusesToCheck = [
      Status.Pending,
      Status.MaterialsReceived,
      Status.OnHold,
      Status.ReadyForReInspection,
    ];
    const relatedPLs = ProjectPunchLists.filter(
      (pl) =>
        pl.ctUuid?.toLocaleUpperCase() === taskId?.toLocaleUpperCase() &&
        statusesToCheck.includes(pl.statusId),
    );
    return relatedPLs.length > 0;
  };

  const handleTaskUpdate = async (e, item, status, successMessage) => {
    e.stopPropagation();
    const Statuses = {
      1: "Open",
      2: "Pass",
      3: "Fail",
      5: "Mark as N/A",
    };

    const relatedPendingPLs = ProjectPunchLists.filter(
      (pl) =>
        pl.ctUuid?.toLocaleUpperCase() === item.ctUuid?.toLocaleUpperCase() &&
        pl.statusId == Status.Pending,
    );

    if (relatedPendingPLs.length && status !== Status.Failed) {
      if (status === Status.NA) {
        toast.error(
          `Cannot mark this task as N/A until all associated punch list items are completed.`,
        );
        return;
      }
      toast.error(
        `Cannot mark ${Statuses[status]} until all associated punch list items are completed.`,
      );
      return;
    }

    const updatedItem = { ...item, statusId: status };

    if (!navigator.onLine) {
      dispatch(
        updateCheckListItem({
          completePath: `${path}${pathDelmiter}${updatedItem.ctUuid}`,
          checklistItem: {
            ...updatedItem,
            notSync: true,
            previousStatusId: item.previousStatusId ?? item.statusId,
          },
        }),
      );
      toast.info("Changes will be synced when you're back online.");
      return;
    }

    try {
      const response = await fetchApi(URLs.updateDD(item.ctUuid), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ statusId: status }),
      });

      if (response.ok) {
        dispatch(
          updateCheckListItem({
            completePath: `${path}${pathDelmiter}${updatedItem.ctUuid}`,
            checklistItem: updatedItem,
          }),
        );
        toast.success(successMessage);
      } else {
        throw new Error("Failed to update the checklist item");
      }
    } catch (error) {
      if (!navigator.onLine) {
        dispatch(
          updateCheckListItem({
            completePath: `${path}${pathDelmiter}${updatedItem.ctUuid}`,
            checklistItem: {
              ...updatedItem,
              notSync: true,
              previousStatusId: item.previousStatusId ?? item.statusId,
            },
          }),
        );
        toast.info("Changes will be synced when you're back online.");
      } else {
        console.error("Failed to update task:", error);
        toast.error("An error occurred while updating the task.");
      }
    }
  };

  const handleSearchChange = (searchTerm: string) => {
    setSearchInspectionTask(searchTerm);
    setLocalStorageItem(searchTermLocalStorageKey, searchTerm);
  };

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);
    setLocalStorageItem(filterLocalStorageKey, filter);
  };

  const handleStatusFilterChange = (statuses: number[]) => {
    setStatusFilter(statuses);
    if (statuses.length > 0) {
      setLocalStorageItem(
        statusFilterLocalStorageKey,
        JSON.stringify(statuses),
      );
    } else {
      removeLocalStorageItem(statusFilterLocalStorageKey);
    }
  };

  const handleIncompletePLFilterChange = (enabled: boolean) => {
    setShowOnlyIncompletePL(enabled);
    if (enabled) {
      setLocalStorageItem(incompletePLFilterLocalStorageKey, true);
    } else {
      removeLocalStorageItem(incompletePLFilterLocalStorageKey);
    }
  };

  const statusCounts = useMemo(() => {
    const counts: Record<number, number> = Object.fromEntries(
      ChecklistStatusDropdownOptions.map((o) => [o.value, 0]),
    );
    const obj = checkListObject ?? {};
    for (const k in obj) {
      const sid = obj[k].statusId;
      if (sid in counts) counts[sid]++;
    }
    return counts;
  }, [checkListObject]);

  const isSearchTermCached =
    searchInspectionTask.length !== 0 ? searchInspectionTask : undefined;

  const statusDropdownOptions = useMemo(
    () =>
      ChecklistStatusDropdownOptions.map((o) => ({
        label: `${o.label} (${statusCounts[o.value] ?? 0})`,
        value: o.value,
      })),
    [statusCounts],
  );

  const statusDropdownValue = useMemo(
    () => statusDropdownOptions.filter((o) => statusFilter.includes(o.value)),
    [statusDropdownOptions, statusFilter],
  );

  const incompletePLTaskCount = useMemo(
    () =>
      Object.keys(checkListObject ?? {}).filter(
        (k) =>
          (incompletePLCounts[checkListObject[k].ctUuid?.toUpperCase()] ?? 0) >
          0,
      ).length,
    [checkListObject, incompletePLCounts],
  );

  const readyToPassTasks = useMemo(() => {
    const completedPLStatuses = [Status.Duplicate, Status.Completed, Status.Cancelled];
    const plsByTask: Record<string, number[]> = {};
    for (const pl of ProjectPunchLists) {
      if (pl.ctUuid && !pl.isArchived) {
        const key = pl.ctUuid.toUpperCase();
        if (!plsByTask[key]) plsByTask[key] = [];
        plsByTask[key].push(pl.statusId);
      }
    }
    const set = new Set<string>();
    for (const [key, statuses] of Object.entries(plsByTask)) {
      if (statuses.length > 0 && statuses.every(s => completedPLStatuses.includes(s))) {
        set.add(key);
      }
    }
    return set;
  }, [ProjectPunchLists]);

  const handleOpenAddTaskModal = () => {
    if (!firstChecklistItem || !path) {
      toast.error("Unable to open add task form for this checklist.");
      return;
    }

    dispatch(
      setModal({
        Chilren: () => (
          <AddChecklistTaskForm firstTask={firstChecklistItem} path={path} />
        ),
        open: true,
        hideModalFooter: true,
      }),
    );
  };

  return (
    <div className="max-w-5xl">
      <Breadcrumb trail={breadcrumbTrail} secondaryTrail={secondaryTrail} />

      <div className="flex my-2">
        <div className="w-full">
          <SearchComponent
            onSearch={handleSearchChange}
            placeHolder="Search inspection task"
            autoComplete="off"
            isSearchTermCached={isSearchTermCached}
            showFilter={filterOptions.length > 0}
            filterOptions={filterOptions}
            onFilterChange={handleFilterChange}
            selectedFilter={selectedFilter}
          />
        </div>

        {bulkEditEnabled && (
          <button
            type="button"
            className="bg-gray-500 text-white text-sm px-4 py-1 ml-2 border rounded-lg"
            title="Exit Bulk Edit"
            onClick={() => {
              setBulkEditEnabled(false);
              setSelectedCheckLists([]);
            }}
          >
            <span className="flex items-center text-sm font-medium">
              <MdOutlineClear />
              <span style={{ whiteSpace: "nowrap" }} className="ml-2">
                Exit Edit
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="flex flex-col md:flex-row gap-5 mb-5">
        {/* Status Filter Dropdown */}
        <button
          type="button"
          className="ml-2 border rounded-lg bg-orange-1000 text-white dark:bg-orange-1000 dark:text-white"
          title="Add Task"
          onClick={handleOpenAddTaskModal}
        >
          <span className="flex items-center text-sm font-medium bg-orange-1000 text-white dark:bg-orange-1000 dark:text-white">
            <FaRegEdit style={{ transform: "rotate(45deg)" }} />
            <span style={{ whiteSpace: "nowrap" }} className="ml-2">
              Add New Task
            </span>
          </span>
        </button>
        <button
          type="button"
          className="ml-2 border rounded-lg bg-orange-1000 text-white dark:bg-orange-1000 dark:text-white"
          title="Bulk Edit"
          hidden={bulkEditEnabled}
          onClick={() => setBulkEditEnabled(!bulkEditEnabled)}
        >
          <span className="flex items-center text-sm font-medium bg-orange-1000 text-white dark:bg-orange-1000 dark:text-white">
            <FaRegEdit />
            <span style={{ whiteSpace: "nowrap" }} className="ml-2">
              Bulk Edit
            </span>
          </span>
        </button>
        <div
          className=""
          style={{ maxWidth: ChecklistStatusDropdownConfig.maxWidth }}
        >
          <Select
            isMulti
            closeMenuOnSelect={false}
            controlShouldRenderValue={true}
            placeholder={ChecklistStatusDropdownConfig.placeholder}
            components={{ Option: CheckboxOption }}
            options={statusDropdownOptions}
            value={statusDropdownValue}
            onChange={(selected) => {
              handleStatusFilterChange(
                selected ? selected.map((s) => s.value) : [],
              );
            }}
            theme={ChecklistStatusDropdownConfig.theme}
          />
        </div>
        <button
          type="button"
          aria-pressed={showOnlyIncompletePL}
          className={`ml-2 border rounded-lg px-3 py-1 text-sm font-medium ${
            showOnlyIncompletePL
              ? "bg-orange-1000 text-white dark:bg-orange-1000 dark:text-white"
              : "bg-gray-50 text-gray-700 dark:bg-gray-900 dark:text-gray-200"
          }`}
          title="Show only tasks with incomplete punch lists"
          onClick={() =>
            handleIncompletePLFilterChange(!showOnlyIncompletePL)
          }
        >
          <span style={{ whiteSpace: "nowrap" }}>
            Incomplete Punch Lists ({incompletePLTaskCount})
          </span>
        </button>
      </div>

      {bulkEditEnabled && (
        <div className="flex flex-col md:flex-row my-2 justify-between">
          <div className="flex flex-col sm:flex-row">
            <span className="flex items-center text-sm font-medium bg-gray-50 px-2 border rounded-lg dark:bg-gray-900 dark:text-gray-200">
              <input
                type="checkbox"
                hidden={!bulkEditEnabled}
                onClick={addOrRemoveAllSelectedItems}
              />
              <span className="ml-3 mr-2">
                Select All ({selectedCheckLists.length} selected)
              </span>
            </span>
            <div className="flex items-center mt-2 md:mt-0">
              <div className="items-center">
                <span className="items-center text-sm font-medium md:ml-4">
                  Select Status:
                </span>
                <select
                  value={currentBulkAction}
                  className="text-sm font-medium border rounded-lg text-black dark:text-gray-200 bg-gray-50 dark:bg-gray-900 py-0.5 px-0.5 md:py-1 md:px-1 ml-2 mt-0 md:mt-2 md:ml-2"
                  onChange={(e) => setCurrentBulkAction(e.target.value)}
                >
                  {Object.keys(BulkActions).map((key) => (
                    <option
                      key={BulkActions[key].id}
                      value={BulkActions[key].id}
                    >
                      {BulkActions[key].name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="border rounded-lg bg-orange-1000 text-white px-4 py-1 text-sm ml-4"
                  title="Confirm"
                  onClick={() => {
                    performBulkOperation();
                    setBulkEditEnabled(false);
                    setSelectedCheckLists([]);
                  }}
                >
                  <span className="flex items-center text-sm font-medium">
                    <GrStatusGood />
                    <span className="ml-2">Save</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <List<DDCheckList>
        name="checklist-dd"
        data={filteredCheckList}
        isItemDisabled={false}
        isItemSelected={isChecklistItemSelected}
        onClick={(item) => handleCheckListClick(item)}
        rowHoverClassName="hover:bg-orange-1000 hover:text-white hover:cursor-pointer"
        virtualize={true}
        scrollToSelectedOnMount={true}
        estimatedItemHeight={80}
        renderChildItem={(item) => (props) => {
          const isDisabled = isTaskDisabled(item.ctUuid);
          return (
            <div
              {...props}
              className={twMerge(
                `flex flex-col md:flex-row justify-start gap-2 text-left`,
                "text-left-wrap",
                isDisabled ? "text-black" : "",
              )}
              onClick={null}
              onKeyDown={null}
              role="button"
              tabIndex={0}
            >
              <div className={`flex items-center gap-2 mr-2`}>
                <input
                  type="checkbox"
                  hidden={!bulkEditEnabled}
                  defaultChecked={selectedCheckLists.some(
                    (selectedItem) =>
                      selectedItem.ctUuid.toLocaleLowerCase() ===
                      item.ctUuid.toLocaleLowerCase(),
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    addOrRemoveSelectedItem(item);
                  }}
                  disabled={isTaskDisabled(item.ctUuid)} // Disable checkbox if the item is disabled
                />

                {/* Passed */}
                <button
                  data-testid="passed-checklist-button"
                  onClick={(e) => {
                    e.stopPropagation();

                    if (isTaskDisabled(item.ctUuid)) {
                      toast.error(
                        `Cannot mark this task as Passed while there are active punch list items.`,
                      );
                    } else {
                      handleTaskUpdate(
                        e,
                        item,
                        Status.Passed,
                        "Task marked as passed successfully.",
                      );
                    }
                  }}
                  className="p-2 -m-2 text-black dark:text-white"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: isTaskDisabled(item.ctUuid)
                      ? "not-allowed"
                      : "pointer",
                    opacity: isTaskDisabled(item.ctUuid) ? 0.2 : 1,
                  }}
                >
                  <FaRegCheckSquare
                    className={`${item.statusId === Status.Passed ? "text-green-500" : ""}`}
                  />
                </button>

                {/* Failed */}
                <button
                  data-testid="failed-checklist-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToPunchList(item);
                  }}
                  className="p-2 -m-2"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <FaRegRectangleXmark
                    className={`${item.statusId === Status.Failed ? "text-red-500" : ""}`}
                  />
                </button>

                {/* N/A */}
                <button
                  data-testid="na-checklist-button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (isTaskDisabled(item.ctUuid)) {
                      toast.error(
                        `Cannot mark this task as N/A while there are active punch list items.`,
                      );
                    } else {
                      await handleTaskUpdate(
                        e,
                        item,
                        Status.NA,
                        "Task marked as not applicable.",
                      );
                    }
                  }}
                  className="p-2 -m-2 text-black dark:text-white"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: isTaskDisabled(item.ctUuid)
                      ? "not-allowed"
                      : "pointer",
                    textDecoration:
                      item.statusId === Status.NA ? "line-through" : "none",
                    opacity: isTaskDisabled(item.ctUuid) ? 0.3 : 1,
                  }}
                >
                  N/A
                </button>

                {/* Reset */}
                <button
                  data-testid="reset-checklist-button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (isTaskDisabled(item.ctUuid)) {
                      toast.error(
                        `Cannot reset this task while there are active punch list items.`,
                      );
                    } else {
                      await handleTaskUpdate(
                        e,
                        item,
                        Status.Open,
                        "Task reset successfully.",
                      );
                    }
                  }}
                  className="p-2 -m-2 text-black dark:text-white"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: isTaskDisabled(item.ctUuid)
                      ? "not-allowed"
                      : "pointer",
                    opacity: isTaskDisabled(item.ctUuid) ? 0.3 : 1,
                  }}
                >
                  <BiReset />
                </button>
              </div>

              <div>
                {
                  // disciplineId == 5 is a mechanical engineer
                  SelectedUser.disciplineId == 5 &&
                    (() => {
                      const itemMPCStatus = MPCStatus.find(
                        (mpc) =>
                          mpc.meChecklistUuid?.toLocaleUpperCase() ===
                          item.ctUuid?.toLocaleUpperCase(),
                      );
                      return (
                        <div className="flex items-center space-x-1 uppercase">
                          <div className="font-bold text-xs uppercase">
                            MPC Status:
                          </div>
                          {NumberToStatus[itemMPCStatus?.mpcStatus] && (
                            <div className="text-xs">
                              {NumberToStatus[itemMPCStatus?.mpcStatus]}
                            </div>
                          )}
                        </div>
                      );
                    })()
                }
                <span>
                  <span
                    style={{
                      color:
                        item.statusId === Status.Passed
                          ? "green"
                          : item.statusId === Status.Failed
                            ? "red"
                            : "var(--checklist-text)",
                      textDecoration:
                        item.statusId === Status.NA ? "line-through" : "none",
                    }}
                  >
                    {item.taskHeader ? (
                      <div
                        className="uppercase text-base text-left-wrap font-bold"
                        id={item.taskHeader}
                      >
                        {item.taskHeader}
                      </div>
                    ) : null}
                    <div className="uppercase text-base text-left-wrap font-semibold">
                      {item.drilldownTypeId === 21
                        ? [
                            item.dd02,
                            item.dd03,
                            item.ap01,
                            item.ap02,
                            item.dd04,
                          ]
                            .filter((v) => v != null && v !== "")
                            .join(" - ")
                        : item.taskDescription}
                    </div>
                  </span>
                </span>
              </div>

              {(() => {
                const plCount = incompletePLCounts[item.ctUuid?.toUpperCase()] ?? 0;
                const readyToPass = item.statusId === Status.Failed && readyToPassTasks.has(item.ctUuid?.toUpperCase());
                if (!plCount && !readyToPass) return null;
                return (
                  <div className="flex items-center flex-shrink-0 ml-auto gap-2">
                    {readyToPass && (
                      <span className="bg-green-200 text-green-800 py-1 px-3 rounded-full text-xs font-semibold whitespace-nowrap">
                        Ready to Pass
                      </span>
                    )}
                    {!!plCount && (
                      <span className="bg-orange-1000 text-white py-1 px-3 rounded-full text-xs font-semibold">
                        {plCount}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        }}
      />
    </div>
  );
};

export default CheckListDD;
